import { useParams } from 'next/navigation';
import { TrashIcon } from '@heroicons/react/24/outline';
import { LineItem } from 'shared/types/wishlist/LineItem';
import Button from 'components/gruenland/atoms/button/Button';
import Image from 'components/gruenland/atoms/image';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import { productTileImage } from 'lib/wardaImages';

export interface WishlistItemProps {
  item: LineItem;
  onRemove?: () => Promise<void>;
  onMoveToCart?: () => Promise<void>;
}

const WishlistItem: React.FC<WishlistItemProps> = ({ item, onRemove, onMoveToCart }) => {
  const { locale } = useParams();

  const { formatMessage } = useFormat({ name: 'gruenland' });

  return (
    <div className="flex max-w-full items-stretch justify-start gap-3 py-[18px] md:gap-[15px]">
      <div className="h-[145px] w-[125px] shrink-0 bg-white p-3">
        <div className="relative size-full">
          <Image
            src={productTileImage(item.variant?.images)}
            suffix="small"
            style={{ objectFit: 'contain' }}
            alt={item.name ?? ''}
            fill
          />
        </div>
      </div>
      <div className="grow">
        <div className="flex max-w-full items-center justify-between">
          <p className="max-w-[150px] overflow-hidden text-ellipsis text-[14px] uppercase leading-loose">{item.name}</p>
          <i onClick={onRemove} className="block cursor-pointer" data-testid="remove-button">
            <TrashIcon stroke="#494949" className="w-5" />
          </i>
        </div>
        <div className="mt-3">
          {item.variant?.discountedPrice ? (
            <div className="flex items-center gap-[5px]">
              <span className="text-[14px] font-medium leading-loose text-accent-red">
                {CurrencyHelpers.formatForCurrency(item.variant.discountedPrice, locale)}
              </span>
              <span className="text-[12px] font-normal leading-loose text-gray-500 line-through">
                {CurrencyHelpers.formatForCurrency(item.variant?.price ?? 0, locale)}
              </span>
            </div>
          ) : (
            <span className="text-[14px] font-medium leading-loose">
              {CurrencyHelpers.formatForCurrency(item.variant?.price ?? 0, locale)}
            </span>
          )}
        </div>
        <div className="mt-4 leading-normal">
          <Button
            variant="buttonLink"
            onClick={onMoveToCart}
            disabled={!item.variant?.isOnStock}
            className="py-2 text-[14px]"
          >
            {formatMessage({ id: 'wishlist.moveToCart', defaultMessage: 'Sposta nel carrello' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WishlistItem;
